import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users,
  MessageSquare,
  Calendar,
  BarChart3,
  Mail,
  ExternalLink,
  Activity,
  TrendingUp,
  Eye,
} from "lucide-react";

interface UserActivityModalProps {
  user: {
    id: string;
    name: string;
    email: string;
    campaignsCreated: number;
    responsesGiven: number;
    totalEngagement: number;
    joinedDate: string;
  } | null;
  isOpen: boolean;
  onClose: () => void;
}

const UserActivityModal: React.FC<UserActivityModalProps> = ({
  user,
  isOpen,
  onClose,
}) => {
  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary to-accent flex items-center justify-center text-white font-semibold">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-xl font-bold">{user.name}</div>
              <div className="text-sm text-muted-foreground">{user.email}</div>
            </div>
          </DialogTitle>
          <DialogDescription>
            Detailed user activity and engagement overview
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* User Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  Campaigns Created
                </span>
              </div>
              <div className="text-2xl font-bold text-blue-900">
                {user.campaignsCreated || 0}
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">
                  Feedback Given
                </span>
              </div>
              <div className="text-2xl font-bold text-green-900">
                {user.responsesGiven || 0}
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-800">
                  Total Engagement
                </span>
              </div>
              <div className="text-2xl font-bold text-purple-900">
                {user.totalEngagement || 0}
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-orange-800">
                  Engagement Rate
                </span>
              </div>
              <div className="text-2xl font-bold text-orange-900">
                {user.totalEngagement > 0
                  ? Math.round(
                      (user.totalEngagement / (user.campaignsCreated || 1)) *
                        100
                    )
                  : 0}
                %
              </div>
            </div>
          </div>

          {/* User Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Users className="w-5 h-5" />
                User Information
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium text-muted-foreground">
                    Member Since
                  </span>
                  <span className="text-sm font-semibold">
                    {new Date(user.joinedDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium text-muted-foreground">
                    Account Status
                  </span>
                  <Badge
                    variant="default"
                    className="bg-green-100 text-green-800"
                  >
                    Active
                  </Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium text-muted-foreground">
                    Last Activity
                  </span>
                  <span className="text-sm font-semibold">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Recent Activity
              </h3>
              <div className="space-y-3">
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium">Latest Feedback</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Provided feedback on "Campaign Name" - 2 days ago
                  </p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <BarChart3 className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium">
                      Campaign Created
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Launched "New Campaign" - 1 week ago
                  </p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Eye className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium">Profile Updated</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Updated profile information - 2 weeks ago
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Engagement Chart Placeholder */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Engagement Trends
            </h3>
            <div className="h-48 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200 flex items-center justify-center">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">
                  Engagement chart would be displayed here
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                window.open(`/admin/users`, "_blank");
              }}
              className="flex-1"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Full Profile
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                window.open(`/admin/moderation`, "_blank");
              }}
              className="flex-1"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              View Campaigns
            </Button>
            <Button onClick={onClose} className="flex-1">
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserActivityModal;
